import { useLocation, useNavigate } from 'react-router';

interface Breadcrumb {
  label: string;
  path: string;
}

export const useBreadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const getSearchParams = (path: string) => {
    const targetUrl = new URL(path, window.location.origin);
    const currentParams = new URLSearchParams(location.search);
    const newParams = new URLSearchParams();

    const breadcrumbs = currentParams.get('br');
    if (breadcrumbs) {
      newParams.set('br', breadcrumbs);
    }

    targetUrl.searchParams.forEach((value, key) => {
      newParams.set(key, value);
    });

    return { url: path.split('?')[0], searchParams: newParams };
  };

  const updateBreadcrumbs = (
    searchParams: URLSearchParams,
    breadcrumbs: string[]
  ) => {
    searchParams.delete('br');
    searchParams.append('br', breadcrumbs.join('|'));
  };

  const addBreadcrumb = ({ path, label }: Breadcrumb) => {
    const { url, searchParams } = getSearchParams(path);
    const existingBreadcrumbs = searchParams.get('br');
    const breadcrumbs = existingBreadcrumbs
      ? existingBreadcrumbs.split('|')
      : [];
    breadcrumbs.push(label);
    updateBreadcrumbs(searchParams, breadcrumbs);
    return `${url}?${searchParams.toString()}`;
  };

  const popBreadcrumb = ({ path }: Breadcrumb) => {
    const { url, searchParams } = getSearchParams(path);
    const breadcrumbParam = searchParams.get('br');

    if (breadcrumbParam) {
      const breadcrumbs = breadcrumbParam.split('|');
      breadcrumbs.pop();
      updateBreadcrumbs(searchParams, breadcrumbs);
    }

    return `${url}?${searchParams.toString()}`;
  };

  const replaceLastBreadcrumb = ({ path, label }: Breadcrumb) => {
    const { url, searchParams } = getSearchParams(path);
    const breadcrumbParam = searchParams.get('br');

    if (breadcrumbParam) {
      const breadcrumbs = breadcrumbParam.split('|');
      if (breadcrumbs.length > 0) {
        breadcrumbs[breadcrumbs.length - 1] = label;
      }
      updateBreadcrumbs(searchParams, breadcrumbs);
    }

    return `${url}?${searchParams.toString()}`;
  };

  const setSingleBreadcrumb = ({ path, label }: Breadcrumb) => {
    const { url, searchParams } = getSearchParams(path);
    searchParams.set('br', label);
    return `${url}?${searchParams.toString()}`;
  };

  const getBreadcrumbs = () => {
    const params = new URLSearchParams(location.search);
    const breadcrumbParam = params.get('br');
    return breadcrumbParam ? breadcrumbParam.split('|') : [];
  };

  const getParentBreadcrumbUrl = (): string | undefined => {
    const params = new URLSearchParams(location.search);
    const breadcrumbParam = params.get('br');
    if (!breadcrumbParam) return undefined;

    const breadcrumbs = breadcrumbParam.split('|');
    // Find the last breadcrumb (before current) that has a URL encoded as Label::URL
    for (let i = breadcrumbs.length - 1; i >= 0; i--) {
      const parts = breadcrumbs[i].split('::');
      if (parts[1]) {
        return parts[1];
      }
    }
    return undefined;
  };

  const addBreadcrumbToUrl = ({ label }: { label: string }): void => {
    const params = new URLSearchParams(location.search);

    const breadcrumbParam = params.get('br');
    const breadcrumbs = breadcrumbParam ? breadcrumbParam.split('|') : [];

    const isLastBreadcrumbDifferent =
      breadcrumbs.length === 0 || breadcrumbs.at(-1) !== label;

    if (isLastBreadcrumbDifferent && breadcrumbs.length === 0) {
      breadcrumbs.push(label);
      params.set('br', breadcrumbs.join('|'));

      const newUrl = `${location.pathname}?${params.toString()}`;
      navigate(newUrl, { replace: true });
    }
  };

  return {
    addBreadcrumb,
    setSingleBreadcrumb,
    popBreadcrumb,
    replaceLastBreadcrumb,
    breadcrumbs: getBreadcrumbs(),
    addBreadcrumbToUrl,
    getParentBreadcrumbUrl,
  };
};
